import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  strippenSaldo: number;
  gebruikStrip: () => boolean;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [strippenSaldo, setStrippenSaldo] = useState(3);

  const fetchAdminStatus = useCallback(async (userId?: string | null) => {
    if (!userId || !supabase) {
      setIsAdmin(false);
      return;
    }

    const { data, error } = await supabase
      .from('admins')
      .select('is_admin')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return;
    }

    setIsAdmin(Boolean(data?.is_admin));
  }, []);

  const resolvePendingStrippenkaarten = useCallback(async (userData?: User | null) => {
    if (!userData?.id || !userData.email || !supabase) {
      return;
    }

    const { data: pending, error } = await supabase
      .from('pending_strippenkaarten')
      .select('id, totaal_strippen')
      .eq('email', userData.email)
      .eq('status', 'pending');

    if (error) {
      console.error('Error fetching pending strippenkaarten:', error);
      return;
    }

    if (!pending || pending.length === 0) {
      return;
    }

    const successfulIds: string[] = [];
    const failedIds: string[] = [];

    for (const entry of pending) {
      const { error: insertError } = await supabase
        .from('strippenkaarten')
        .insert({
          user_id: userData.id,
          resterende_strippen: entry.totaal_strippen,
          totaal_strippen: entry.totaal_strippen,
        });

      if (insertError) {
        console.error('Error linking pending strippenkaart:', insertError);
        failedIds.push(entry.id);
        continue;
      }

      const { error: updateError } = await supabase
        .from('pending_strippenkaarten')
        .update({
          status: 'redeemed',
          redeemed_at: new Date().toISOString(),
        })
        .eq('id', entry.id);

      if (updateError) {
        console.error('Error updating pending strippenkaart status:', updateError);
        failedIds.push(entry.id);
        continue;
      }

      successfulIds.push(entry.id);
    }

    if (successfulIds.length > 0) {
      toast.success('Je openstaande strippenkaarten zijn aan je account gekoppeld.');
    }

    if (failedIds.length > 0) {
      toast.error('Een deel van de openstaande strippenkaarten kon niet gekoppeld worden. Neem contact op.');
    }
  }, []);

  useEffect(() => {
    // Check active session
    let isMounted = true;

    const initializeSession = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!isMounted) {
        return;
      }

      if (error) {
        console.error('Error fetching session:', error);
      }

      setSession(session);
      setUser(session?.user ?? null);
      await fetchAdminStatus(session?.user?.id);
      await resolvePendingStrippenkaarten(session?.user ?? null);
      setLoading(false);
    };

    initializeSession();

    // Listen for auth changes
    if (supabase) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        fetchAdminStatus(session?.user?.id);
        resolvePendingStrippenkaarten(session?.user ?? null);
        setLoading(false);
      });

      return () => {
        isMounted = false;
        subscription.unsubscribe();
      };
    } else {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }
  }, [fetchAdminStatus, resolvePendingStrippenkaarten]);

  const signUp = async (email: string, password: string) => {
    if (!supabase) {
      return { error: { message: 'Supabase not configured', status: 500 } as AuthError };
    }
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { error: { message: 'Supabase not configured', status: 500 } as AuthError };
    }
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setStrippenSaldo(3); // Reset saldo bij uitloggen
  };

  const gebruikStrip = (): boolean => {
    if (strippenSaldo > 0) {
      setStrippenSaldo(strippenSaldo - 1);
      return true;
    }
    return false;
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    strippenSaldo,
    gebruikStrip,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

